import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PublicTopicsPage from './pages/PublicTopicsPage';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import AdminJudgingPage from './pages/AdminJudgingPage';
import './styles/globals.css';

// Public pages
import LandingPage from './components/public/LandingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';

// Dashboard layout
import DashboardLayout from './components/dashboard/DashboardLayout';

// Dashboard pages
import DashboardHome from './pages/DashboardHome';
import MarketplacePage from './pages/MarketplacePage';
import ApplicationsPage from './pages/ApplicationsPage';
import ProjectsPage from './pages/ProjectsPage';
import EnterFinalePage from './pages/EnterFinalePage';
import SponsorshipsPage from './pages/SponsorshipsPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import TopicApplicationsPage from './pages/TopicApplicationsPage';

// Admin pages
import { AdminUsersPage, AdminTopicsPage, AdminConfigPage, AdminRenewalPage, AdminReceiptsPage } from './pages/AdminPages';
import AdminSocialPage from './pages/AdminSocialPage';
import AdminCheckinPage from './pages/AdminCheckinPage';
import LecturerTopicsPage from './pages/LecturerTopicsPage';
import AlumniTopicsPage from './pages/AlumniTopicsPage';
import StudentTopicsPage from './pages/StudentTopicsPage';
import Season1Page from './pages/Season1Page';
import Season2Page from './pages/Season2Page';
import SponsorshipPage from './pages/SponsorshipPage';
import ResultsPage from './pages/ResultsPage';
import { ThemeProvider } from './hooks/useTheme';
import HallOfHonourPage from './pages/HallOfHonourPage';
import FAQPage from './pages/FAQPage';
import { AnalyticsPage, RankingsPage } from './pages/AnalyticsPages';

// Judging pages
import { JudgingListPage, JudgeScoringPage } from './pages/JudgingPages';

import { LoadingPage } from './components/shared';
import ShowcasePage from './pages/ShowcasePage';
import RenewalPage from './pages/RenewalPage';
import CertificatesPage from './pages/CertificatesPage';
import VerifyPage from './pages/VerifyPage';
import GuidePage from './pages/GuidePage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import FinaleRegistrationPage from './pages/FinaleRegistrationPage';
import CheckinPage from './pages/CheckinPage';
import VotePage from './pages/VotePage';
import FeedbackPage from './pages/FeedbackPage';
import PollAdminPage from './pages/PollAdminPage';
import FeedbackAdminPage from './pages/FeedbackAdminPage';

// Route guards
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingPage />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};
const RoleSplit = ({ judgeEl, adminEl }) => {
  const { user } = useAuth();
  return user?.role === 'admin' ? adminEl : judgeEl;
};

const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPage />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth */}
      <Route path="/finale" element={<FinaleRegistrationPage />} />
      <Route path="/checkin/:reference" element={<CheckinPage />} />
      <Route path="/vote" element={<VotePage />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />

      {/* Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardHome />} />

        {/* Shared */}
        <Route path="marketplace" element={<MarketplacePage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="guide" element={<GuidePage />} />

        {/* Student */}
        <Route path="applications" element={
          <ProtectedRoute roles={['student', 'admin']}>
            <ApplicationsPage />
          </ProtectedRoute>
        } />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="enter-finale" element={
          <ProtectedRoute roles={['student']}>
            <EnterFinalePage />
          </ProtectedRoute>
        } />

        {/* Lecturer */}
        <Route path="topics" element={
          <ProtectedRoute roles={['admin']}>
            <AdminTopicsPage />
          </ProtectedRoute>
        } />
        <Route path="my-topics" element={
          <ProtectedRoute roles={['lecturer']}>
            <LecturerTopicsPage />
          </ProtectedRoute>
        } />
        <Route path="alumni-topics" element={
          <ProtectedRoute roles={['alumni']}>
            <AlumniTopicsPage />
          </ProtectedRoute>
        } />
        <Route path="student-topics" element={
          <ProtectedRoute roles={['student']}>
            <StudentTopicsPage />
          </ProtectedRoute>
        } />
        <Route path="topics/:id/applications" element={
          <ProtectedRoute roles={['admin','lecturer']}>
            <TopicApplicationsPage />
          </ProtectedRoute>
        } />

        {/* Alumni */}
        <Route path="sponsorships" element={
          <ProtectedRoute roles={['alumni', 'judge', 'admin']}>
            <SponsorshipsPage />
          </ProtectedRoute>
        } />

        {/* Admin */}
        <Route path="users" element={
          <ProtectedRoute roles={['admin']}>
            <AdminUsersPage />
          </ProtectedRoute>
        } />
        <Route path="receipts" element={
          <ProtectedRoute roles={['admin']}>
            <AdminReceiptsPage />
          </ProtectedRoute>
        } />
        <Route path="social" element={
          <ProtectedRoute roles={['admin']}>
            <AdminSocialPage />
          </ProtectedRoute>
        } />
        <Route path="event-checkins" element={
          <ProtectedRoute roles={['admin']}>
            <AdminCheckinPage />
          </ProtectedRoute>
        } />
        <Route path="renewal" element={
          <ProtectedRoute roles={['admin']}>
            <AdminRenewalPage />
          </ProtectedRoute>
        } />
        <Route path="config" element={
          <ProtectedRoute roles={['admin']}>
            <AdminConfigPage />
          </ProtectedRoute>
        } />
        <Route path="analytics" element={
          <ProtectedRoute roles={['admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="rankings" element={
          <ProtectedRoute roles={['admin']}>
            <RankingsPage />
          </ProtectedRoute>
        } />
        <Route path="polls" element={
          <ProtectedRoute roles={['admin']}>
            <PollAdminPage />
          </ProtectedRoute>
        } />
        <Route path="feedback-admin" element={
          <ProtectedRoute roles={['admin']}>
            <FeedbackAdminPage />
          </ProtectedRoute>
        } />

        {/* Judge */}
        <Route path="judging" element={
          <ProtectedRoute roles={['judge', 'admin']}>
            <RoleSplit judgeEl={<JudgingListPage />} adminEl={<AdminJudgingPage />} />
          </ProtectedRoute>
        } />
        <Route path="judging/:id" element={
          <ProtectedRoute roles={['judge', 'admin']}>
            <JudgeScoringPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch-all */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/season1" element={<Season1Page />} />
      <Route path="/season2" element={<Season2Page />} />
      <Route path="/sponsor" element={<SponsorshipPage />} />
      <Route path="/topics" element={<PublicTopicsPage />} />
      <Route path="/honour" element={<HallOfHonourPage />} />
      <Route path="/results" element={<ResultsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/showcase/:slug" element={<ShowcasePage />} />
      <Route path="/renew/:token" element={<RenewalPage />} />
      <Route path="/verify/:certId" element={<VerifyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: 'var(--ink-mid)',
              color: 'var(--text-primary)',
              border: '1px solid rgba(255,255,255,0.1)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              borderRadius: '8px',
            },
            success: {
              iconTheme: { primary: 'var(--teal)', secondary: 'var(--ink-deep)' }
            },
            error: {
              iconTheme: { primary: '#ef4444', secondary: 'var(--ink-deep)' }
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}